import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useForm } from "react-hook-form";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { CirclePlus, ImageUp, Loader } from "lucide-react";
import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { toast } from "sonner";
import { getUsersTenantData, registerUserUnities } from "@/Api/unities";
import { getSelectRoles } from "@/Api/roles";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Form as FormComponent } from "@/components/Form";

export const AddUserBtn = ({ updateFront, uuidUnit }) => {
  const form = useForm();
  const formWatch = form.watch();

  const [isLoad, setIsLoad] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [roles, setRoles] = useState([]);

  useEffect(() => {
    if (isOpen) {
      fetchRoles();
    }
  }, [isOpen]);

  const fetchRoles = async () => {
    try {
      const { data } = await getSelectRoles();
      setRoles(data?.response || []);
    } catch (error) {
      console.error("Erro ao buscar roles:", error);
    }
  };

  const formSubmit = async (data) => {
    setIsLoad((prev) => !prev);
    const roles: string[] = [];
    if (data?.role_id) {
      roles.push(String(data?.role_id));
    }
    const payload = {
      ...data,
      photo: data?.photo?.[0] || null,
      password: data?.password,
      roles: roles,
    };

    const { data: apiResponse, ok } = await registerUserUnities(
      uuidUnit,
      payload
    );
    if (!ok) {
      toast("Error ao registrar o usuário!");
      setIsLoad((prev) => !prev);
      setIsOpen((prev) => !prev);
      return;
    }

    const { data: refreshData } = await getUsersTenantData(uuidUnit);
    updateFront(refreshData?.response);

    toast(apiResponse?.message);
    setIsLoad((prev) => !prev);
    setIsOpen((prev) => !prev);
  };

  useEffect(() => {
    if (!isOpen) form.reset();
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger>
        <Button
          variant="outline"
          className={cn("py-3 flex gap-1", `bg-primary`)}
        >
          <CirclePlus className="size-5" strokeWidth={1.75} />
          Adicionar Usuário
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[450px]">
        {/* <ScrollArea> */}
        <DialogHeader className="flex flex-col gap-2">
          <DialogDescription className="text-lg">
            Adicionar Usuário
          </DialogDescription>
          <DialogTitle className="text-2xl">Informações Básicas</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form className="space-y-3" onSubmit={form.handleSubmit(formSubmit)}>
            <FormItem className="flex flex-col items-center gap-1">
              <FormLabel className={cn("flex gap-2")}>Perfil:</FormLabel>
              <div className="flex items-center rounded-full size-32 border border-dashed border-gray-900/25 dark:border-gray-500 relative">
                <div className="flex text-sm/6 text-gray-600 h-full w-full">
                  <label
                    htmlFor="file-upload"
                    className="w-full h-full flex items-center justify-center cursor-pointer font-semibold text-blue-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-blue-600 focus-within:ring-offset-2 group rounded-full overflow-hidden"
                  >
                    {formWatch?.photo?.length > 0 && (
                      <img
                        className="absolute top-0 left-0 aspect-square object-contain w-full h-full -z-1 group-hover:brightness-75 transition rounded-full"
                        src={URL.createObjectURL(formWatch?.photo?.[0])}
                        alt="profile_image"
                      />
                    )}
                    <span className="flex gap-2 items-center relative z-50">
                      {!(formWatch?.photo?.length > 0) ? (
                        <ImageUp className="size-7 group-hover:text-blue-700" />
                      ) : (
                        <ImageUp className="size-7 opacity-0 transition group-hover:opacity-100" />
                      )}
                    </span>
                    <input
                      id="file-upload"
                      type="file"
                      className="sr-only"
                      accept="image/*"
                      {...form.register("photo")}
                    />
                  </label>
                </div>
              </div>
            </FormItem>
            <FormField
              control={form.control}
              name="name"
              rules={{ required: "Campo Obrigatório" }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Nome:
                    <FormMessage />
                  </FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Insira o nome" />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              rules={{
                required: "Campo Obrigatório",
                pattern: { value: /^\S+@\S+$/i, message: "Formato inválido." },
              }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    E-mail:
                    <FormMessage />
                  </FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Insira o e-mail" />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              rules={{ required: "Campo Obrigatório" }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Senha:
                    <FormMessage />
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      typeMask="password"
                      placeholder="Digite a senha"
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="document"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Documento:
                    <FormMessage />
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      typeMask="cpfOrCnpj"
                      placeholder="Insira o documento"
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Telefone:
                    <FormMessage />
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      typeMask="phone"
                      placeholder="Insira o telefone"
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormComponent.Field name="role_id">
              {({ field }) => (
                <FormComponent.Item>
                  <FormComponent.Label>
                    Cargo <FormComponent.Message />
                  </FormComponent.Label>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormComponent.Control>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione um Cargo" />
                      </SelectTrigger>
                    </FormComponent.Control>
                    <SelectContent>
                      {roles.map((item: any) => (
                        <SelectItem key={item.id} value={String(item.id)}>
                          {item.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormComponent.Item>
              )}
            </FormComponent.Field>
            <DialogFooter className="mt-5">
              <Button
                type="submit"
                className={cn("flex gap-2 mt-1", colors.bg_primary)}
                disabled={isLoad}
              >
                {isLoad ? (
                  <>
                    <Loader className="animate-spin size-4" /> Adicionando
                  </>
                ) : (
                  <>Adicionar</>
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
        {/* </ScrollArea> */}
      </DialogContent>
    </Dialog>
  );
};
